// src/lib/api.js
// ALL requests use Content-Type: text/plain to avoid CORS preflight (Azure Functions v4 bug).
// See dev log 2026-03-17 for full explanation.

const API_BASE = import.meta.env.VITE_API_URL || "https://masterfileai-api.azurewebsites.net";

import { getToken, clearSession } from "./auth";

export async function apiFetch(path, options = {}) {
  const { method = "GET", body, headers: extraHeaders = {} } = options;

  const headers = {
    "Content-Type": "text/plain",          // NEVER change to application/json
    ...extraHeaders,
  };

  const token = getToken();
  // GET: token passed as ?token= query param. POST: no auth header (avoids CORS preflight).

  const config = {
    method,
    headers,
  };

  if (body !== undefined) {
    config.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  // Token always sent as query param — safe for all methods, avoids Authorization header (CORS preflight)
  const tokenSuffix = token ? (path.includes("?") ? "&" : "?") + "token=" + token : "";
  const res = await fetch(`${API_BASE}${path}${tokenSuffix}`, config);

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = { message: text };
  }

  // Handle 401 — only redirect if we had an active session (not during login)
  if (res.status === 401 && getToken()) {
    clearSession();
    window.location.href = "/login";
    throw new Error("Session expired. Please log in again.");
  }

  if (!res.ok) {
    const e = new Error(data?.message || data?.error || `HTTP ${res.status}`);
    if (data?.warn)    e.warn    = true;
    if (data?.blocked) e.blocked = true;
    if (data?.attempts_remaining != null) e.attempts_remaining = data.attempts_remaining;
    throw e;
  }

  return data;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export const authRegister = (payload) =>
  apiFetch("/api/auth/register", { method: "POST", body: payload });

export const authLogin = (email, password) =>
  apiFetch("/api/auth/login", { method: "POST", body: { email, password } });

export const authVerify2FA = (user_id, code) =>
  apiFetch("/api/auth/verify-2fa", { method: "POST", body: { user_id, code } });

export const authLogout = () =>
  apiFetch("/api/auth/logout", { method: "POST" });

export const authMe = () =>
  apiFetch("/api/auth/me");

export const updateProfile = (payload) =>
  apiFetch("/api/auth/profile", { method: "PUT", body: payload });

export const changePassword = (current_password, new_password) =>
  apiFetch("/api/auth/change-password", { method: "POST", body: { current_password, new_password } });

// ── Team ──────────────────────────────────────────────────────────────────────

export const listTeam = () =>
  apiFetch("/api/team");

export const inviteTeamMember = (email, role) =>
  apiFetch("/api/team/invite", { method: "POST", body: { email, role } });

export const removeTeamMember = (userId) =>
  apiFetch(`/api/team/${userId}`, { method: "DELETE" });

export const updateMemberRole = (userId, role) =>
  apiFetch(`/api/team/${userId}/role`, { method: "PUT", body: { role } });

export const getInviteInfo = (token) =>
  apiFetch(`/api/team/invite-info?token=${token}`);

export const acceptInvite = (token, full_name, password) =>
  apiFetch("/api/team/accept-invite", { method: "POST", body: { token, full_name, password } });

// ── Jobs ──────────────────────────────────────────────────────────────────────

export const getJobs = () =>
  apiFetch("/api/jobs");

export const getJob = (jobId) =>
  apiFetch(`/api/jobs/${jobId}`);

export const deleteJob = (jobId) =>
  apiFetch(`/api/jobs/${jobId}/delete`, { method: "DELETE" });

// ── Upload ────────────────────────────────────────────────────────────────────

export const uploadFile = (payload) =>
  apiFetch("/api/upload", { method: "POST", body: payload });

// ── Job rows ───────────────────────────────────────────────────────────────────

export const getJobRows = (jobId, { page = 1, limit = 100, q = "" } = {}) => {
  const params = new URLSearchParams({ page, limit });
  if (q) params.set("q", q);
  return apiFetch(`/api/jobs/${jobId}/rows?${params}`);
};

// ── Tenant domains ─────────────────────────────────────────────────────────────

export const getTenantDomains = () =>
  apiFetch("/api/tenant/domains");

export const addTenantDomain = (domain) =>
  apiFetch("/api/tenant/domains/add", { method: "POST", body: { domain } });

export const removeTenantDomain = (domain) =>
  apiFetch("/api/tenant/domains/remove", { method: "POST", body: { domain } });

// ── Results ────────────────────────────────────────────────────────────────────

export const getJobResults = (jobId, { page = 1, limit = 100 } = {}) => {
  const params = new URLSearchParams({ page, limit });
  return apiFetch(`/api/jobs/${jobId}/results?${params}`);
};

export const getResultsSummary = (jobId) =>
  apiFetch(`/api/jobs/${jobId}/results/summary`);

export const getResultsRecords = (jobId, { filter = "all", sector = "", page = 1, limit = 50 } = {}) => {
  const params = new URLSearchParams({ filter, page, limit });
  if (sector) params.set("sector", sector);
  return apiFetch(`/api/jobs/${jobId}/results/records?${params}`);
};

export const getResultsDetail = (jobId, rowNumber, { filter = "all", sector = "" } = {}) => {
  const params = new URLSearchParams({ filter });
  if (sector) params.set("sector", sector);
  return apiFetch(`/api/jobs/${jobId}/results/records/${rowNumber}?${params}`);
};

// ── Process ────────────────────────────────────────────────────────────────────

export const processJob = (jobId, { limit = 0 } = {}) => {
  const qs = limit > 0 ? `?limit=${limit}` : "";
  return apiFetch(`/api/jobs/${jobId}/process${qs}`, { method: "POST", body: {} });
};

// ── Pricing ────────────────────────────────────────────────────────────────────

export const getJobPricing = (jobId, mode = 'all') =>
  apiFetch(`/api/jobs/${jobId}/pricing?mode=${mode}`);

// ── Payments ───────────────────────────────────────────────────────────────────

export const createPaymentSession = (jobId, discountCode) =>
  apiFetch("/api/payments/create-session", {
    method: "POST",
    body: { job_id: jobId, ...(discountCode ? { discount_code: discountCode } : {}) },
  });

export const validateDiscountCode = (jobId, code) =>
  apiFetch("/api/payments/validate-discount", {
    method: "POST",
    body: { job_id: jobId, code },
  });

// ── Billing ────────────────────────────────────────────────────────────────────

export const getBillingHistory = () => apiFetch("/api/billing");

export const resendReceipt = (jobId) =>
  apiFetch(`/api/billing/${jobId}/receipt`, { method: "POST", body: {} });

// ── Master File ─────────────────────────────────────────────────────────────────

export const getMasterFileSummary    = () => apiFetch("/api/master-file/summary");
export const getMasterFileDuplicates = () => apiFetch("/api/master-file/duplicates");
export const getMasterFileExport     = () => apiFetch("/api/master-file/export");

export const getMasterFileRecords = ({ filter = "all", sector = "", country = "", state = "", search = "", missing = false, page = 1, limit = 50, sort_col = "", sort_dir = "" } = {}) => {
  const params = new URLSearchParams({ filter, page, limit });
  if (sector)   params.set("sector",    sector);
  if (country)  params.set("country",   country);
  if (state)    params.set("state",     state);
  if (search)   params.set("search",    search);
  if (missing)  params.set("missing",   "1");
  if (sort_col) params.set("sort_col",  sort_col);
  if (sort_dir) params.set("sort_dir",  sort_dir);
  return apiFetch(`/api/master-file/records?${params}`);
};

// ── Surveys (public — no auth) ────────────────────────────────────────────────
export const getSurvey    = (token)           => apiFetch(`/api/survey/${token}`);
export const submitSurvey = (token, answers)  => apiFetch(`/api/survey/${token}/submit`, { method: "POST", body: { answers } });

export const markWalkthroughSeen = () => apiFetch("/api/auth/walkthrough-seen", { method: "POST", body: {} });

export const submitFeedback = (feedback_text, page_url) =>
  apiFetch("/api/feedback", { method: "POST", body: { feedback_text, page_url } });
